import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  Badge,
  Body1,
  Button,
  Caption1,
  Card,
  Link,
  MessageBar,
  MessageBarBody,
  Spinner,
  Subtitle1,
  Subtitle2,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  Title2,
  makeStyles,
  mergeClasses,
  tokens,
} from '@fluentui/react-components';
import type { CampaignView, CaseComparison } from '@lg/core';
import type { ReadinessLayer } from '@lg/shared';
import { useApi } from '../api/client';
import { config } from '../config';
import { errorMessage, shortDate } from '../ui/format';
import { KIND_LABEL, LAYER_LABEL } from '../readiness/format';

const useStyles = makeStyles({
  head: { display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: tokens.spacingHorizontalM },
  spacer: { flex: 1 },
  intro: { maxWidth: '78ch', marginBlock: tokens.spacingVerticalS },
  stack: { display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalL },
  card: {
    padding: tokens.spacingHorizontalL,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
  counts: { display: 'flex', flexWrap: 'wrap', gap: tokens.spacingHorizontalS },
  wrap: { overflowX: 'auto' },
  mono: { fontFamily: tokens.fontFamilyMonospace, fontSize: tokens.fontSizeBase200, whiteSpace: 'nowrap' },
  sub: { display: 'block', color: tokens.colorNeutralForeground3, fontSize: tokens.fontSizeBase200 },
  cell: { textAlign: 'center', fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' },
  clean: { backgroundColor: tokens.colorPaletteGreenBackground1 },
  bad: { backgroundColor: tokens.colorPaletteRedBackground1, fontWeight: tokens.fontWeightSemibold },
  empty: { color: tokens.colorNeutralForeground4 },
});

const LAYERS: ReadinessLayer[] = ['L1', 'L2', 'L3', 'L4'];

/** /testing/campaigns: before-and-after comparison for a D365 update (build update 2.4, section 10). */
export function CampaignsPage() {
  const styles = useStyles();
  const api = useApi();
  const navigate = useNavigate();
  const list = useQuery({ queryKey: ['campaigns'], queryFn: () => api.listCampaigns() });

  return (
    <div>
      <div className={styles.head}>
        <Title2 as="h1">Upgrade campaigns</Title2>
        <span className={styles.spacer} />
        <Button onClick={() => navigate('/testing')}>Test library</Button>
      </div>
      <Body1 as="p" className={styles.intro}>
        Before a Microsoft update reaches D365 TEST, every venue&apos;s settings and one venue per
        archetype&apos;s access and process tests run as the baseline. After the update the same plans run
        again; each case is compared, and each failure is split into a UI change or a behavior change.
      </Body1>
      {list.isPending && <Spinner label="Loading campaigns…" />}
      {list.isError && (
        <MessageBar intent="error">
          <MessageBarBody>{errorMessage(list.error)}</MessageBarBody>
        </MessageBar>
      )}
      <div className={styles.stack}>
        {list.data?.map((c) => (
          <CampaignCard key={c.campaign.id} view={c} />
        ))}
        {list.data?.length === 0 && <Body1>No campaigns yet.</Body1>}
      </div>
    </div>
  );
}

function CampaignCard({ view }: { view: CampaignView }) {
  const styles = useStyles();
  const { campaign, comparison } = view;
  const findings = (comparison?.cases ?? []).filter(
    (c) =>
      c.classification === 'Regression' ||
      c.classification === 'StillFailing' ||
      c.classification === 'Fixed',
  );
  return (
    <Card className={styles.card}>
      <div className={styles.head}>
        <Subtitle1 as="h2">{campaign.name}</Subtitle1>
        <Badge appearance="outline">
          {campaign.fromVersion} → {campaign.toVersion}
        </Badge>
        <Badge appearance="tint" color={campaign.status === 'Compared' ? 'brand' : 'informative'}>
          {campaign.status}
        </Badge>
        <span className={styles.spacer} />
        <Caption1>Started {shortDate(campaign.createdAt)}</Caption1>
      </div>
      {comparison ? (
        <>
          <div className={styles.counts} aria-label="Comparison counts">
            <Badge appearance="filled" color={comparison.counts.Regression ? 'danger' : 'success'}>
              {comparison.counts.Regression} regression{comparison.counts.Regression === 1 ? '' : 's'}
            </Badge>
            <Badge appearance="tint" color="warning">
              {comparison.counts.StillFailing} still failing
            </Badge>
            <Badge appearance="tint" color="success">
              {comparison.counts.Fixed} fixed
            </Badge>
            <Badge appearance="tint" color="informative">
              {comparison.counts.NewCase} new
            </Badge>
            <Badge appearance="tint" color="subtle">
              {comparison.counts.Skipped} skipped
            </Badge>
            <Badge appearance="tint" color="subtle">
              {comparison.counts.Unchanged} unchanged
            </Badge>
          </div>

          <Subtitle2 as="h3">Venues × layers</Subtitle2>
          <div className={styles.wrap}>
            <Table size="small" aria-label="Regressions by venue and layer">
              <TableHeader>
                <TableRow>
                  <TableHeaderCell>Venue</TableHeaderCell>
                  {LAYERS.map((l) => (
                    <TableHeaderCell key={l} className={styles.cell}>
                      {l} {LAYER_LABEL[l]}
                    </TableHeaderCell>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {comparison.matrix.map((m) => (
                  <TableRow key={m.company}>
                    <TableCell>
                      <b className={styles.mono}>{m.company}</b>
                      <span className={styles.sub}>{view.venueNames[m.company] ?? ''}</span>
                    </TableCell>
                    {LAYERS.map((l) => {
                      const cell = m.layers[l];
                      return (
                        <TableCell
                          key={l}
                          className={mergeClasses(
                            styles.cell,
                            cell.cases === 0 ? styles.empty : cell.regressions ? styles.bad : styles.clean,
                          )}
                        >
                          {cell.cases === 0
                            ? 'not in scope'
                            : cell.regressions
                              ? `${cell.regressions} regression${cell.regressions > 1 ? 's' : ''} / ${cell.cases}`
                              : `${cell.cases} ok`}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>

          <Subtitle2 as="h3">Findings</Subtitle2>
          {findings.length === 0 ? (
            <Body1>No differences between baseline and candidate.</Body1>
          ) : (
            <div className={styles.wrap}>
              <Table size="small" aria-label="Campaign findings">
                <TableHeader>
                  <TableRow>
                    <TableHeaderCell style={{ width: '24%' }}>Case</TableHeaderCell>
                    <TableHeaderCell style={{ width: '7%' }}>Venue</TableHeaderCell>
                    <TableHeaderCell style={{ width: '11%' }}>Before → after</TableHeaderCell>
                    <TableHeaderCell style={{ width: '11%' }}>Classification</TableHeaderCell>
                    <TableHeaderCell style={{ width: '40%' }}>What happened</TableHeaderCell>
                    <TableHeaderCell style={{ width: '7%' }}>Evidence</TableHeaderCell>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {findings.map((f) => (
                    <Finding key={`${f.company}|${f.caseId}`} f={f} />
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
          <Caption1>
            A UI change is a control the test could not find: one selector fix repairs every case that uses
            it. A behavior change is a failed assertion: a real finding for the release decision.
          </Caption1>
        </>
      ) : (
        <Body1>Baseline recorded. Run the candidate after the update reaches TEST.</Body1>
      )}
    </Card>
  );
}

function Finding({ f }: { f: CaseComparison }) {
  const styles = useStyles();
  const link =
    f.traceUrl ??
    (config.demoReportsUrl && f.after === 'failed'
      ? `${config.demoReportsUrl}/index.html#?q=${encodeURIComponent(f.caseId)}`
      : null);
  return (
    <TableRow>
      <TableCell>
        <span className={styles.mono} style={{ whiteSpace: 'normal', wordBreak: 'break-all' }}>
          {f.caseId}
        </span>
        <span className={styles.sub}>
          {f.template} · {f.layer} {LAYER_LABEL[f.layer]}
        </span>
      </TableCell>
      <TableCell className={styles.mono}>{f.company}</TableCell>
      <TableCell>
        {f.before ?? '—'} → {f.after}
      </TableCell>
      <TableCell>
        <Badge
          appearance="tint"
          color={
            f.classification === 'Regression'
              ? 'danger'
              : f.classification === 'Fixed'
                ? 'success'
                : 'warning'
          }
        >
          {f.classification === 'StillFailing' ? 'Still failing' : f.classification}
        </Badge>
        {f.failureKind && <span className={styles.sub}>{KIND_LABEL[f.failureKind] ?? f.failureKind}</span>}
      </TableCell>
      <TableCell>{f.errorSummary ?? '—'}</TableCell>
      <TableCell>
        {link ? (
          <Link href={link} target="_blank" rel="noopener">
            Trace
          </Link>
        ) : (
          '—'
        )}
      </TableCell>
    </TableRow>
  );
}
